import React, { useMemo, useEffect } from "react";
import { useZStore } from "@/utils/z-store";
import { DashboardDropdownActiveIcon } from "@/assets/svg-icons/svg-icons";
import ReusableDropdown from "./ReusableDropdown";
import cn from "@/utils/cn";
import axios from "axios";
import { NewDashboardData } from "@/app/zustand-store/slices/dashboardSlice";

interface DashboardDropdownProps {
  showCreateOption?: boolean;
  invertColors?: boolean; // to keep background white on home page and background blue on edit dashboard page
  className?: string;
  disabled?: boolean;
}

const DashboardDropdown: React.FC<DashboardDropdownProps> = ({
  showCreateOption = false,
  invertColors = false,
  className = "",
  disabled = false,
}) => {
  const { data, actions } = useZStore();

  const dashboards: any[] = useMemo(() => {
    // Handle both null and undefined cases
    if (!data?.dashboardData || !data?.dashboardData.length) return [];

    return data.dashboardData.map((dashboard) => {
      let isEditable = true;
      if (dashboard.type === "shared" && dashboard.invitedBy) {
        const userRole = data.userDetails?.roles?.find(
          (role: any) => role.account === dashboard.invitedBy,
        );
        isEditable = userRole?.role === "member";
      }

      // Format name: remove leading asterisks, replace all hyphens/underscores with spaces, and capitalize
      let cleanName = dashboard.name || "";

      // Remove leading asterisks which might be used as system markers
      cleanName = cleanName.replace(/^[*]+/, "");

      // Replace all hyphens and underscores with spaces
      cleanName = cleanName.replace(/[-_]+/g, " ");

      // Normalize whitespace and trim
      cleanName = cleanName.replace(/\s+/g, " ").trim();

      if (cleanName) {
        // Capitalize the first letter of each word
        cleanName = cleanName.replace(/\b\w/g, (char) => char.toUpperCase());
      }

      return {
        id: dashboard.dashboardId,
        name: cleanName || dashboard.name, // Fallback to original if empty
        type: dashboard.type || "unknown",
        isEditable,
      };
    });
  }, [data.dashboardData, data.userDetails?.roles]);

  const selectedDashboardId =
    data.selectedDashboard || data?.dashboardData?.[0]?.dashboardId;

  // Check if the selected dashboard exists in the dashboards array
  const isSelectedDashboardValid = data?.dashboardData?.some(
    (d) => d.dashboardId === data.selectedDashboard,
  );

  // Auto-select the first dashboard if none is selected or if selected dashboard is invalid
  useEffect(() => {
    if (
      data?.dashboardData?.length > 0 &&
      (!data.selectedDashboard || !isSelectedDashboardValid)
    ) {
      actions.setSelectedDashboard(data.dashboardData[0].dashboardId);
    }
  }, [
    data.selectedDashboard,
    data.dashboardData,
    actions,
    isSelectedDashboardValid,
  ]);

  const handleSelect = (id: string) => {
    actions.setSelectedDashboard(id);
  };

  const handleAdd = (name: string): string => {
    const newDashboardId = actions.addDashboard(name);
    return newDashboardId;
  };

  const handleUpdate = async (id: string, name: string) => {
    try {
      await axios.put(`/api/users/dashboard`, { dashboardId: id, name: name });
      actions.updateDashboardName(id, name);
    } catch (error) {
      console.error("Error updating dashboard:", error);
      if (axios.isAxiosError(error)) {
        console.error("API Error:", error.response?.data);
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // Perform a single DELETE request
      const response = await axios.delete(`/api/users/dashboard`, {
        data: { dashboardId: id },
      });

      // Update the selected dashboard based on response.data.selectedDashboard
      // The API returns { message, selectedDashboard }
      if (response.data.selectedDashboard) {
        actions.setSelectedDashboard(response.data.selectedDashboard);
      } else {
        // Fallback: select the first remaining dashboard
        const remainingDashboards = dashboards.filter((d) => d.id !== id);
        if (remainingDashboards.length > 0) {
          actions.setSelectedDashboard(remainingDashboards[0].id);
        }
      }

      // Remove the dashboard from Zustand via actions.deleteDashboard(id)
      // This should be done after updating the selected dashboard to ensure proper state transition
      actions.deleteDashboard(id);
    } catch (error) {
      console.error("Error deleting dashboard:", error);
      // If the API call fails, we should revert the local state changes
      // This is a simplified approach - in a real app, you might want more sophisticated error handling

      // Show error feedback to the user
      if (axios.isAxiosError(error)) {
        console.error("API Error:", error.response?.data);
        // Could add toast notification here
      }
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      // First update the local state
      actions.duplicateDashboard(id);

      // Then sync with the server
      const response = await axios.post("/api/users/dashboard", {
        dashboardId: id,
      });

      // Update the state with the server response to ensure consistency
      actions.setDashboardData(response.data.data);
      actions.setSelectedDashboard(response.data.selectedDashboard);
    } catch (error) {
      console.error("Error duplicating dashboard:", error);
    }
  };

  // NEW: Handle reordering
  const handleReorder = async (newOrder: any[]) => {
    // Map the new order back to full dashboard objects
    const reorderedDashboards = newOrder
      .map((item) => data.dashboardData.find((d) => d.dashboardId === item.id))
      .filter(
        (dashboard): dashboard is NewDashboardData => dashboard !== undefined,
      );

    // Update Zustand store
    actions.reorderDashboards(reorderedDashboards);

    // Update backend
    try {
      await axios.put(
        "/api/dashboards/reorder",
        {
          dashboardData: reorderedDashboards,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Dashboard order updated successfully");
    } catch (error) {
      console.error("Error updating dashboard order:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error:", error.response?.data);
      }
    }
  };

  return (
    <ReusableDropdown
      title="Dashboard"
      selectedItemId={selectedDashboardId}
      items={dashboards}
      onSelect={handleSelect}
      onAdd={handleAdd}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onReorder={handleReorder}
      IconOpen={DashboardDropdownActiveIcon}
      IconClosed={DashboardDropdownActiveIcon}
      placeholder="Enter dashboard name"
      minNameLength={3}
      className={cn("", className)}
      showCreateOption={showCreateOption}
      invertColors={invertColors}
      disabled={disabled}
      hideDeleteForSelected={true}
    />
  );
};

export default DashboardDropdown;
